import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Logo } from '@/components/brand/Logo'
import { Button } from '@/components/ui/Button'

export function SigninPage() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle')
  const [devLink, setDevLink] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.includes('@')) return
    setStatus('loading')
    try {
      const res = await fetch('/api/auth/magic-link/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, intent: 'signin' }),
      })
      const data = await res.json() as { ok: boolean; message: string; dev_link?: string; error?: string }
      if (data.ok) {
        setStatus('sent')
        if (import.meta.env.DEV && data.dev_link) setDevLink(data.dev_link)
      } else {
        setStatus('error')
        setErrorMsg(data.error ?? 'Something went wrong. Please try again.')
      }
    } catch {
      setStatus('error')
      setErrorMsg('Could not connect. Please try again.')
    }
  }

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <div className="p-6">
        <Link to="/"><Logo size="sm" /></Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-sm">
          {status === 'sent' ? (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-echo-mark/10 rounded-full flex items-center justify-center mx-auto">
                <span className="text-echo-mark text-xl font-medium">✓</span>
              </div>
              <h1 className="text-2xl font-medium text-deep-ink">Check your email</h1>
              <p className="text-slate-500 text-sm">
                We sent a sign-in link to <strong className="text-deep-ink">{email}</strong>.
              </p>
              {devLink && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-left">
                  <p className="text-xs font-medium text-amber-700 mb-1">Dev mode — magic link:</p>
                  <a href={devLink} className="text-xs text-amber-600 hover:underline break-all">{devLink}</a>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-medium text-deep-ink mb-2">Sign in</h1>
                <p className="text-slate-500 text-sm">We'll email you a magic link — no password needed.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-deep-ink mb-1.5" htmlFor="signin-email">
                    Email address
                  </label>
                  <input
                    id="signin-email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@yourcompany.com"
                    required
                    autoFocus
                    className="w-full px-4 py-2.5 border border-border-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-echo-mark/30 focus:border-echo-mark bg-white placeholder:text-slate-400"
                  />
                </div>
                {status === 'error' && (
                  <p className="text-sm text-risk-rose">{errorMsg}</p>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={status === 'loading' || !email.includes('@')}
                >
                  {status === 'loading' ? 'Sending…' : 'Send magic link →'}
                </Button>
                {!email.includes('@') && (
                  <p className="text-xs text-ink/50 text-center">Enter your email address to enable.</p>
                )}
              </form>
              <p className="text-center text-sm text-slate-500 mt-6">
                Don't have an account?{' '}
                <Link to="/signup" className="text-echo-mark hover:underline font-medium">Get started free</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
